/**
 * Wave 38 · Digital LED-style Countdown Timer (DD:HH:MM:SS).
 * Auto-updates every second. Color shifts as expiry approaches.
 *   > 6h  → green (safe)
 *   1-6h  → amber (heating)
 *   < 1h  → red pulsing (urgent)
 *   ≤ 0   → "EXPIRED" flip
 */
import React, { useState, useEffect, useMemo } from 'react';

function pad(n) { return String(n).padStart(2, '0'); }

function parseIso(iso) {
  if (!iso) return null;
  try { const d = new Date(iso); if (isNaN(d.getTime())) return null; return d.getTime(); }
  catch (_) { return null; }
}

export default function CountdownTimer({ expiresAt, compact = false, onExpire }) {
  const target = useMemo(() => parseIso(expiresAt), [expiresAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  useEffect(() => {
    if (target && now >= target && onExpire) onExpire();
  }, [target, now, onExpire]);

  if (!target) return null;
  const diff = Math.max(0, Math.floor((target - now) / 1000));
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = diff % 60;

  const expired = diff <= 0;
  const totalHours = diff / 3600;
  let tone = 'safe';           // > 6h
  if (totalHours <= 1) tone = 'urgent';
  else if (totalHours <= 6) tone = 'warn';
  if (expired) tone = 'expired';

  const colors = {
    safe:   { text: 'text-emerald-300', glow: 'shadow-emerald-500/30', border: 'border-emerald-400/40', bg: 'bg-emerald-950/40' },
    warn:   { text: 'text-amber-300',   glow: 'shadow-amber-500/40',   border: 'border-amber-400/50',   bg: 'bg-amber-950/40' },
    urgent: { text: 'text-rose-300',    glow: 'shadow-rose-500/50',    border: 'border-rose-400/60',    bg: 'bg-rose-950/40' },
    expired:{ text: 'text-slate-500',   glow: '',                       border: 'border-slate-700',      bg: 'bg-slate-900/40' },
  }[tone];

  if (expired) {
    return (
      <div data-testid="atc-countdown-expired"
           className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono font-black tabular-nums text-[10px] uppercase tracking-widest ${colors.text} ${colors.bg} ${colors.border} border`}>
        ⏱ Expired
      </div>
    );
  }

  const pulseClass = tone === 'urgent' ? 'animate-pulse' : '';
  if (compact) {
    return (
      <div data-testid="atc-countdown-compact"
           className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-mono font-black tabular-nums text-[10px] ${colors.text} ${colors.bg} ${colors.border} border ${pulseClass}`}>
        {days > 0 && <><span>{pad(days)}</span><span className="opacity-60">d</span></>}
        <span>{pad(hours)}</span><span className="opacity-40">:</span>
        <span>{pad(mins)}</span><span className="opacity-40">:</span>
        <span>{pad(secs)}</span>
      </div>
    );
  }

  return (
    <div data-testid="atc-countdown-full"
         className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-mono font-black tabular-nums shadow-lg ${colors.text} ${colors.bg} ${colors.border} ${colors.glow} border-2 ${pulseClass}`}>
      <Segment value={pad(days)} label="D" />
      <span className="opacity-50 mx-0.5">:</span>
      <Segment value={pad(hours)} label="H" />
      <span className="opacity-50 mx-0.5">:</span>
      <Segment value={pad(mins)} label="M" />
      <span className="opacity-50 mx-0.5">:</span>
      <Segment value={pad(secs)} label="S" pulse />
    </div>
  );
}

function Segment({ value, label, pulse }) {
  return (
    <span className="inline-flex flex-col items-center leading-none">
      <span className={`text-sm ${pulse ? 'atc-timer-tick' : ''}`}>{value}</span>
      <span className="text-[7px] opacity-60 uppercase tracking-widest">{label}</span>
    </span>
  );
}
